import { pdf } from "@react-pdf/renderer";
import AssessmentPDF from "../components/PDF/AssessmentPDF";
import {
  getAllAssessment,
  getAnswersByBlockAndUser,
  getCompleteDate,
  getExtraAnswer,
} from "../axios/axiosFunctions";

const ANSWER_SCORES = {
  notConfident: 1,
  somewhat: 2,
  confident: 3,
  very: 4,
};

const stripTags = (text = "") =>
  text.replace(/<\/?u>/gi, "").replace(/<\/?strong>/gi, "");

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const buildBlockData = async (blockId, userId, questionCount) => {
  const res = await getAnswersByBlockAndUser(blockId, userId);
  if (!res?.answers) return { chart: [], note: "" };

  const answers = [...res.answers];
  let note = "";
  if (answers.length > questionCount) {
    const last = answers.pop();
    note = Object.prototype.hasOwnProperty.call(ANSWER_SCORES, last?.answer)
      ? ""
      : last?.answer || "";
  }

  const chart = answers.map((a) => ({
    resp: ANSWER_SCORES[a.answer] || 0,
    question: stripTags(a.question?.text || ""),
  }));

  return { chart, note };
};

const SELF_SLUGS = new Set(["mim_self_assessment", "lim_self_assessment"]);

export const downloadSelfAssessmentPDF = async ({ userId, slug, name }) => {
  if (!SELF_SLUGS.has(slug)) {
    throw new Error(`Unsupported self-assessment slug: ${slug}`);
  }

  const allAssessments = await getAllAssessment();
  const assessment = (allAssessments || []).find((a) => a.slug === slug);
  if (!assessment) {
    throw new Error(`Assessment ${slug} not found`);
  }

  const blocks = assessment.blocks || [];
  if (blocks.length === 0) {
    throw new Error(`Assessment ${slug} has no blocks`);
  }

  const [completeDateRes, extraQRes, ...blockResults] = await Promise.all([
    getCompleteDate(userId, slug),
    getExtraAnswer(slug, userId),
    ...blocks.map((b) =>
      buildBlockData(b._id, userId, (b.questions || []).length),
    ),
  ]);

  const chartData = {};
  const blockNotes = {};
  blocks.forEach((b, i) => {
    chartData[b._id] = blockResults[i].chart;
    blockNotes[b._id] = blockResults[i].note;
  });

  const dateCompleted = formatDate(completeDateRes?.dateCompleted);
  const extraQ = extraQRes?.answer?.answer || "";

  const blob = await pdf(
    <AssessmentPDF
      chartData={chartData}
      blocks={blocks}
      nameA={assessment.name}
      slug={assessment.slug}
      report={assessment.report}
      summaryIntroImg={null}
      sectionsIntroImg={null}
      info={assessment?.report?.blocksInfo || []}
      name={name}
      blockNotes={blockNotes}
      dateCompleted={dateCompleted}
      extraQ={extraQ}
    />,
  ).toBlob();

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const safeName = (name || "user").trim().replace(/\s+/g, "_") || "user";
  link.download = `${slug}__${safeName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
